import { useMutation, useQuery } from '@tanstack/react-query'
import { customerCreateSchema, customerPatchSchema, reachableChannels } from '@today/models'
import { ContactPointType, CustomerChannel } from '@today/types'
import type { ContactDraft, CustomerFormValues } from '@today/ui-domain'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useViewerPermissions } from '@/auth/useViewerPermissions'
import { type CustomerDTO, customerService } from '@/services/customer.service'
import { fieldError, unitOptions } from './customer-ui'

const EMPTY: CustomerFormValues = {
	first_name: '',
	last_name_1: '',
	last_name_2: '',
	type: 'lead',
	status: 'active',
	preferred_channel: '',
	preferred_locale: '',
	tags: '',
	org_unit_id: '',
	assigned_user_id: '',
}

const fromCustomer = (c: CustomerDTO): CustomerFormValues => ({
	first_name: c.firstName,
	last_name_1: c.lastName1 ?? '',
	last_name_2: c.lastName2 ?? '',
	type: c.type,
	status: c.status,
	preferred_channel: c.preferredChannel ?? '',
	preferred_locale: c.preferredLocale ?? '',
	tags: c.tags.join(', '),
	org_unit_id: c.orgUnit.id,
	assigned_user_id: c.assignedUser.id,
})

/** Snake_case profile body shared by create and edit (empty optional values become null). */
export const profileBody = (v: CustomerFormValues) => ({
	first_name: v.first_name,
	last_name_1: v.last_name_1 || null,
	last_name_2: v.last_name_2 || null,
	type: v.type,
	status: v.status,
	preferred_channel: v.preferred_channel || null,
	preferred_locale: v.preferred_locale || null,
	tags: v.tags
		.split(',')
		.map((tag) => tag.trim())
		.filter(Boolean),
})

/** Every preferred channel with whether the entered contacts make it reachable (same rule as the API). */
export const channelOptions = (contacts: ContactDraft[], existingTypes: string[]) => {
	const types = [...contacts.filter((c) => c.value.trim()).map((c) => c.type), ...existingTypes] as ContactPointType[]
	const reachable = reachableChannels(types)
	return Object.values(CustomerChannel).map((id) => ({ id, enabled: reachable.includes(id) }))
}

type Values = CustomerFormValues
type Duplicate = { visible: Array<{ id: string; label: string }>; hidden: number } | null

/** Client-side required fields (the server re-validates everything). */
const requiredErrors = (values: Values, mode: 'create' | 'edit', message: string): Record<string, string> => {
	const errors: Record<string, string> = {}
	if (!values.first_name.trim()) errors.first_name = message
	if (mode === 'create' && !values.org_unit_id) errors.org_unit_id = message
	if (mode === 'create' && !values.assigned_user_id) errors.assigned_user_id = message
	return errors
}

/** The create body: profile + assignment + non-empty contacts + chosen initial marketing consents. */
const createBody = (values: Values, contacts: ContactDraft[], consentChannels: string[]) => ({
	...profileBody(values),
	org_unit_id: values.org_unit_id,
	assigned_user_id: values.assigned_user_id,
	contact_points: contacts.filter((c) => c.value.trim()).map(({ type, value, label }) => ({ type, value, label })),
	...(consentChannels.length
		? { consents: consentChannels.map((channel) => ({ channel, purpose: 'marketing', status: 'granted' })) }
		: {}),
})

/** Duplicate lookup of one contact (email/mobile only): visible matches + invisible count, or null. */
const lookupDuplicate = async (contact: ContactDraft | undefined): Promise<Duplicate> => {
	if (!contact?.value.trim() || contact.type === 'landline') return null
	try {
		const result = await customerService.lookup(contact.type, contact.value)
		const visible = result.data.map((m) => ({ id: m.customerId, label: m.fullName }))
		return visible.length || result.outOfScopeMatches ? { visible, hidden: result.outOfScopeMatches } : null
	} catch {
		return null
	}
}

/** Form draft state and its change handlers. */
function useFormDraft(existing: CustomerDTO | undefined) {
	const [values, setValues] = useState<Values>(EMPTY)
	const [contacts, setContacts] = useState<ContactDraft[]>([
		{ id: 'contact-0', type: 'email', value: '', label: 'personal', is_primary: true },
	])
	const [consentChannels, setConsentChannels] = useState<string[]>([])
	useEffect(() => {
		if (existing) setValues(fromCustomer(existing))
	}, [existing])
	return {
		values,
		contacts,
		consentChannels,
		onChange: (field: keyof Values, value: string) => setValues({ ...values, [field]: value }),
		onContactChange: (index: number, field: keyof ContactDraft, value: string | boolean) =>
			setContacts(contacts.map((c, i) => (i === index ? { ...c, [field]: value } : c))),
		onAddContact: () =>
			setContacts([
				...contacts,
				{ id: `contact-${contacts.length}`, type: 'mobile', value: '', label: 'personal', is_primary: false },
			]),
		onToggleConsent: (channel: string) =>
			setConsentChannels(
				consentChannels.includes(channel)
					? consentChannels.filter((c) => c !== channel)
					: [...consentChannels, channel],
			),
	}
}

/** The customer being edited (if any) and the unit / user pickers. */
function useFormReferenceData(id: string | undefined) {
	const { tenantSlug } = useViewerPermissions()
	const existing = useQuery({ queryKey: ['customer', id], queryFn: () => customerService.get(id ?? ''), enabled: !!id })
	const units = useQuery({ queryKey: ['org-units'], queryFn: customerService.orgUnits })
	const users = useQuery({
		queryKey: ['tenant-users', tenantSlug],
		queryFn: () => customerService.users(tenantSlug ?? ''),
		enabled: !!tenantSlug,
	})
	return {
		existing,
		orgUnits: unitOptions(units.data ?? []),
		users: (users.data ?? []).map((u) => ({ id: u.userId, label: u.displayName })),
	}
}

/**
 * Create/edit form state (TO-418 customer-ui REQ-005): client validation with the
 * shared `@today/models` schemas, reachable channels, the duplicate lookup on
 * contact blur, and server machine codes mapped onto fields.
 */
export function useCustomerForm() {
	const { t, i18n } = useTranslation()
	const navigate = useNavigate()
	const { id } = useParams()
	const mode: 'create' | 'edit' = id ? 'edit' : 'create'
	const { existing, orgUnits, users } = useFormReferenceData(id)
	const draft = useFormDraft(existing.data)
	const [errors, setErrors] = useState<Record<string, string>>({})
	const [duplicate, setDuplicate] = useState<Duplicate>(null)
	const submit = useMutation({
		mutationFn: () =>
			mode === 'edit'
				? customerService.update(id ?? '', customerPatchSchema.parse(profileBody(draft.values)))
				: customerService.create(
						customerCreateSchema.parse(createBody(draft.values, draft.contacts, draft.consentChannels)),
					),
		onSuccess: (customer) => navigate(`/customers/${customer.customerId}`),
		onError: (error) => {
			const { field, message } = fieldError(error, t, (k) => i18n.exists(k))
			setErrors({ [field]: message })
		},
	})
	return {
		t,
		mode,
		...draft,
		errors,
		formError: errors.form ?? null,
		channels: channelOptions(
			mode === 'create' ? draft.contacts : [],
			existing.data ? existingTypes(existing.data) : [],
		),
		orgUnits,
		users,
		typeLocked: existing.data?.type === 'customer',
		duplicate,
		isBusy: submit.isPending,
		onContactBlur: async (index: number) => setDuplicate(await lookupDuplicate(draft.contacts[index])),
		onSubmit: () => {
			const next = requiredErrors(draft.values, mode, t('form.required'))
			setErrors(next)
			if (Object.keys(next).length === 0) submit.mutate()
		},
		onCancel: () => navigate(id ? `/customers/${id}` : '/customers'),
	}
}

/** Contact types an existing customer already has (its primaries), for edit-mode reachability. */
const existingTypes = (c: CustomerDTO): string[] => [
	...(c.primaryEmail ? [ContactPointType.EMAIL] : []),
	...(c.primaryMobile ? [ContactPointType.MOBILE] : []),
	// A landline-only customer keeps `phone` reachable; the server re-checks with every live contact.
	...(!c.primaryEmail && !c.primaryMobile ? [ContactPointType.LANDLINE] : []),
]
